import { z } from 'zod'

export const TASK_STATUSES = ['todo', 'in-progress', 'waiting', 'done'] as const

export const TASK_PRIORITIES = ['low', 'normal', 'high'] as const

export const AUTOMATIC_TASK_COMPLETION_RULES = ['wedding-date-set', 'budget-total-set'] as const

export const TASK_LINKED_SECTIONS = ['wedding-identity', 'budget', 'guests'] as const

export const taskStatusSchema = z.enum(TASK_STATUSES)
export const taskPrioritySchema = z.enum(TASK_PRIORITIES)
export const automaticTaskCompletionRuleSchema = z.enum(AUTOMATIC_TASK_COMPLETION_RULES)
export const taskLinkedSectionSchema = z.enum(TASK_LINKED_SECTIONS)

const optionalText = (max: number) =>
  z.preprocess(
    (value) => (typeof value === 'string' && value.trim() === '' ? undefined : value),
    z.string().trim().max(max).optional(),
  )

const clearableOptionalText = (max: number) =>
  z.preprocess(
    (value) => (typeof value === 'string' && value.trim() === '' ? null : value),
    z.string().trim().max(max).nullable().optional(),
  )

const optionalDate = z.preprocess(
  (value) => (typeof value === 'string' && value.trim() === '' ? undefined : value),
  z.iso.date().optional(),
)

const clearableOptionalDate = z.preprocess(
  (value) => (typeof value === 'string' && value.trim() === '' ? null : value),
  z.iso.date().nullable().optional(),
)

const clearableDate = z.preprocess(
  (value) => (typeof value === 'string' && value.trim() === '' ? null : value),
  z.iso.date().nullable(),
)

const assigneeMemberIdSchema = z.preprocess(
  (value) => (typeof value === 'string' && value.trim() === '' ? null : value),
  z.string().trim().min(1).nullable(),
)

const planningTaskIdsSchema = (max: number) =>
  z
    .array(z.uuid())
    .min(1)
    .max(max)
    .refine((taskIds) => new Set(taskIds).size === taskIds.length)

export const createPlanningTaskSchema = z.object({
  title: z.string().trim().min(1).max(180),
  status: taskStatusSchema.default('todo'),
  priority: taskPrioritySchema.default('normal'),
  dueDate: optionalDate,
  assigneeMemberId: assigneeMemberIdSchema.default(null),
  notes: optionalText(3000),
  labelNames: z.array(z.string().trim().min(1).max(60)).max(20).default([]),
  sortOrder: z.number().int().min(0).max(10_000).default(0),
  context: z
    .discriminatedUnion('type', [
      z.object({ type: z.literal('venue'), id: z.uuid() }),
      z.object({ type: z.literal('ceremony') }),
      z.object({ type: z.literal('reception') }),
      z.object({ type: z.literal('photo') }),
    ])
    .optional(),
})

export const initialPlanningTaskSchema = createPlanningTaskSchema
  .pick({
    title: true,
    priority: true,
    notes: true,
    labelNames: true,
  })
  .extend({
    automaticCompletionRule: automaticTaskCompletionRuleSchema.optional(),
    linkedSection: taskLinkedSectionSchema.optional(),
  })

export type InitialPlanningTaskInput = z.infer<typeof initialPlanningTaskSchema>

export const updatePlanningTaskSchema = z.object({
  title: z.string().trim().min(1).max(180).optional(),
  status: taskStatusSchema.optional(),
  priority: taskPrioritySchema.optional(),
  dueDate: clearableOptionalDate,
  assigneeMemberId: assigneeMemberIdSchema.optional(),
  notes: clearableOptionalText(3000),
  labelNames: z.array(z.string().trim().min(1).max(60)).max(20).optional(),
})

export const planningTaskBulkActionSchema = z.object({
  taskIds: planningTaskIdsSchema(1_000),
  action: z.discriminatedUnion('type', [
    z.object({ type: z.literal('assign'), assigneeMemberId: assigneeMemberIdSchema }),
    z.object({ type: z.literal('delete') }),
    z.object({ type: z.literal('status'), status: taskStatusSchema }),
    z.object({ type: z.literal('due-date'), dueDate: clearableDate }),
  ]),
})

export type TaskStatus = (typeof TASK_STATUSES)[number]
export type TaskPriority = (typeof TASK_PRIORITIES)[number]
export type AutomaticTaskCompletionRule = (typeof AUTOMATIC_TASK_COMPLETION_RULES)[number]
export type TaskLinkedSection = (typeof TASK_LINKED_SECTIONS)[number]
export type CreatePlanningTaskInput = z.infer<typeof createPlanningTaskSchema>
export type UpdatePlanningTaskInput = z.infer<typeof updatePlanningTaskSchema>
export type PlanningTaskBulkActionInput = z.infer<typeof planningTaskBulkActionSchema>
